export type ServiceTreeNode = {
  id: number;
  name: string;
  children: ServiceTreeNode[];
};

function normalizeServiceName(value: unknown): string {
  return String(value ?? "")
    .replace(/&#(?:x([\da-f]+)|(\d+));/gi, (_match, hex, decimal) => {
      const codePoint = Number.parseInt(hex ?? decimal, hex ? 16 : 10);
      try {
        return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : "";
      } catch {
        return "";
      }
    })
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function normalizeServiceTree(input: unknown): ServiceTreeNode[] {
  const pickRows = (value: unknown): unknown[] => {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== "object") return [];

    const raw = value as Record<string, unknown>;
    const nested =
      raw.data ?? raw.result ?? raw.results ?? raw.categories ?? raw.list;

    if (Array.isArray(nested)) return nested;
    if (nested && typeof nested === "object") return [nested];
    if ("id" in raw && "name" in raw) return [raw];
    return [];
  };

  const parse = (row: unknown): ServiceTreeNode | null => {
    if (!row || typeof row !== "object") return null;
    const raw = row as Record<string, unknown>;
    const id = Number(raw.id);
    const name = normalizeServiceName(raw.name);
    if (!Number.isInteger(id) || id <= 0 || !name) return null;

    const childRows = Array.isArray(raw.children) ? raw.children : [];
    const children = childRows
      .map(parse)
      .filter((node): node is ServiceTreeNode => node !== null);

    return { id, name, children };
  };

  return pickRows(input)
    .map(parse)
    .filter((node): node is ServiceTreeNode => node !== null);
}
